import React, { useEffect, useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';

type Props = {
  onSuccess: (payload: any) => void;
  onError?: (err: any) => void;
};

const loadScript = (src: string, id?: string) =>
  new Promise<void>((resolve, reject) => {
    if (id && document.getElementById(id)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    if (id) s.id = id;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load " + src));
    document.head.appendChild(s);
  });

const SocialAuthButtons: React.FC<Props> = ({ onSuccess, onError }) => {
  const googleClient = useRef<any>(null);
  const fbId = "867635465868559";
  const gId = "972531676318-2pa6k5uuo0h6e4aiiji1pble23l5m0cf.apps.googleusercontent.com";
  useEffect(() => {
    // load Google Identity Services
    const googleId = process.env.REACT_APP_GOOGLE_CLIENT_ID || gId;
    if (googleId) {
      loadScript("https://accounts.google.com/gsi/client", "gsi-client")
        .then(() => {
          // token client will be created on demand in signInWithGoogle
        })
        .catch((e) => {
          console.error("Google SDK load error", e);
        });
    }

    // load Facebook SDK
    const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || fbId;
    if (fbAppId) {
      // @ts-ignore
      if (!window.fbAsyncInit) {
        // @ts-ignore
        window.fbAsyncInit = function () {
          // @ts-ignore
          window.FB.init({
            appId: fbAppId,
            cookie: true,
            xfbml: false,
            version: "v16.0",
          });
        };
      }
      loadScript("https://connect.facebook.net/en_US/sdk.js", "facebook-jssdk").catch((e) =>
        console.error("FB SDK load error", e)
      );
    }
  }, []);

  const signInWithGoogle = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || gId;
    if (!clientId) {
      const err = new Error("REACT_APP_GOOGLE_CLIENT_ID not set");
      if (onError) onError(err);
      return;
    }
    try {
      // create token client (requests access_token for userinfo)
      if (!(window as any).google) throw new Error("Google Identity Services not loaded");
      if (!googleClient.current) {
        googleClient.current = (window as any).google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: "openid email profile",
          callback: (resp: any) => {
            // callback will be handled below - but leave empty to satisfy API
          },
        });
      }

      // request token via popup
      googleClient.current.requestAccessToken({
        prompt: "consent", // or 'none' to avoid re-consent
      });

      // listen for token response using one-time callback: use "callback" by re-init with a wrapper
      // simpler: poll google.accounts.oauth2.hasGrantedAllScopes isn't reliable; instead use an onmessage-less flow:
      // create a promise wrapper around token reception by temporarily re-init with callback
      const token = await new Promise<string>((resolve, reject) => {
        // re-init token client with callback capturing token
        const tc = (window as any).google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: "openid email profile",
          callback: (resp: any) => {
            if (resp.error) return reject(resp);
            if (!resp.access_token) return reject(new Error("No access_token returned"));
            resolve(resp.access_token);
          },
        });
        // request token (this opens popup)
        tc.requestAccessToken({ prompt: "consent" });
      });

      // fetch profile
      const profileRes = await fetch(`https://www.googleapis.com/oauth2/v3/userinfo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!profileRes.ok) throw new Error("Failed to fetch Google profile");
      const profile = await profileRes.json();
      onSuccess({ provider: "google", token, profile });
    } catch (err) {
      console.error("Google sign-in error", err);
      if (onError) onError(err);
    }
  };

  const signInWithFacebook = async () => {
    const fbAppId = process.env.REACT_APP_FACEBOOK_APP_ID || fbId;
    if (!fbAppId) {
      const err = new Error("REACT_APP_FACEBOOK_APP_ID not set");
      if (onError) onError(err);
      return;
    }
    try {
      if (!(window as any).FB) throw new Error("Facebook SDK not loaded");
      // Request email + public_profile and ask to return granted scopes
      (window as any).FB.login(
        (response: any) => {
          if (response.status !== "connected") {
            if (onError) onError(response);
            return;
          }
          const accessToken = response.authResponse.accessToken;
          const granted = response.authResponse.grantedScopes || response.grantedScopes || "";
          // If email not granted, ask to re-request email
          if (!granted.includes("email")) {
            // try re-requesting email permission
            (window as any).FB.login(
              (resp2: any) => {
                if (resp2.status !== "connected") {
                  if (onError) onError(resp2);
                  return;
                }
                const token2 = resp2.authResponse.accessToken;
                // fetch profile after re-request
                (window as any).FB.api("/me", { fields: "id,name,email,picture" }, (profileResp: any) => {
                  if (!profileResp || profileResp.error) {
                    if (onError) onError(profileResp);
                    return;
                  }
                  // if email still missing, surface a clear error
                  if (!profileResp.email) {
                    if (onError) onError({ error: "missing_email", message: "Email permission not granted. Please enable email for Facebook login." });
                    return;
                  }
                  onSuccess({ provider: "facebook", token: token2, profile: profileResp });
                });
              },
              { scope: "email,public_profile", return_scopes: true, auth_type: "rerequest" } // re-request email
            );
            return;
          }
          // fetch profile
          (window as any).FB.api("/me", { fields: "id,name,email,picture" }, (profileResp: any) => {
            if (!profileResp || profileResp.error) {
              if (onError) onError(profileResp);
              return;
            }
            if (!profileResp.email) {
              // email not present despite granted scopes — surface helpful error
              if (onError) onError({ error: "missing_email", message: "Facebook did not return email. Use another login or check Facebook account settings." });
              return;
            }
            onSuccess({ provider: "facebook", token: accessToken, profile: profileResp });
          });
        },
        { scope: "email,public_profile", return_scopes: true }
      );
    } catch (err) {
      console.error("Facebook sign-in error", err);
      if (onError) onError(err);
    }
  };

  const signInWithLinkedIn = () => {
    // LinkedIn client-only flows are limited / deprecated. recommend server or a hosted auth provider.
    const err = new Error("LinkedIn client-only flow is not supported reliably. Use server or Auth provider.");
    if (onError) onError?.(err);
  };

  return (
    
    <Row className="mb-3 login-btns" style={{ marginLeft: "0" }}>
        <Form.Group as={Col} xs="4" controlId="validationCustom10">
            <div className='align-center btn-out margin-0'>
                <Button onClick={signInWithFacebook} variant="light">Facebook</Button>
            </div>
        </Form.Group>
        <Form.Group as={Col} xs="4"  controlId="validationCustom11">
            <div className='align-center btn-out margin-0'>
                <Button onClick={signInWithLinkedIn} variant="light">Linked in</Button>
            </div>
        </Form.Group>
        <Form.Group as={Col} xs="4" controlId="validationCustom12">
            <div className='align-center btn-out margin-0 google-login'>
                {/* <Button onClick={() => handleLoginUser('Google')} variant="light">Google</Button> */}
                <Button onClick={signInWithGoogle} variant="light">Google</Button>
            </div>
        </Form.Group>
    </Row>
  );
};

export default SocialAuthButtons;